import type { BlockDefinition, LinkListItem, LinkListProps } from '../types';
import { ensureHttps, escapeHtml, maybeTokenize, safeUrl } from '../html';

/**
 * Link list — multiple labeled rows with optional thumbnails + captions.
 * Different from a stack of `link-button`s because each row is wider and
 * supports an image, a sub-line, and a chevron, which feels less
 * "button-y" and more "list-y" — the look most multi-link bios go for.
 */
export const linkList: BlockDefinition<LinkListProps> = {
  type: 'link-list',
  meta: {
    label: 'Link list',
    icon: '📋',
    category: 'buttons',
    description: 'Stacked list of links with optional thumbnails + captions.',
  },
  defaults: () => ({
    items: [
      { label: 'Latest video', url: 'https://', thumbnail: '', caption: '' },
      { label: 'My shop', url: 'https://', thumbnail: '', caption: '' },
    ],
  }),
  validate(input) {
    const i = (input ?? {}) as Partial<LinkListProps>;
    const raw = Array.isArray(i.items) ? i.items : [];
    return {
      items: raw
        .map(validateItem)
        .filter((x): x is LinkListItem => x !== null)
        .slice(0, 40),
    };
  },
  render(props, _theme, ctx) {
    if (props.items.length === 0) return '';
    const rows = props.items
      .map((it) => {
        const href = maybeTokenize(safeUrl(ensureHttps(it.url)), ctx?.tokenizeUrl);
        const thumb = it.thumbnail
          ? `<img src="${safeUrl(it.thumbnail)}" alt="" loading="lazy" decoding="async" style="width:48px;height:48px;border-radius:12px;object-fit:cover;flex-shrink:0;background:rgba(0,0,0,0.06);"/>`
          : '<div style="width:48px;height:48px;flex-shrink:0;"></div>';
        const cap = it.caption
          ? `<div style="font-size:12px;color:var(--text);opacity:0.6;margin-top:2px;">${escapeHtml(it.caption)}</div>`
          : '';
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="display:flex;align-items:center;gap:12px;padding:12px;border-radius:var(--radius-card);background:rgba(0,0,0,0.04);text-decoration:none;color:var(--text);transition:transform 0.1s,background 0.15s;" onmouseover="this.style.background='rgba(0,0,0,0.06)'" onmouseout="this.style.background='rgba(0,0,0,0.04)'">
  ${thumb}
  <div style="min-width:0;flex:1;">
    <div style="font-size:15px;font-weight:600;">${escapeHtml(it.label)}</div>
    ${cap}
  </div>
  <div style="color:var(--text);opacity:0.4;font-size:18px;">›</div>
</a>`;
      })
      .join('');
    return `<div class="pb-block pb-link-list" style="display:flex;flex-direction:column;gap:8px;">${rows}</div>`;
  },
};

function validateItem(input: unknown): LinkListItem | null {
  if (!input || typeof input !== 'object') return null;
  const i = input as Partial<LinkListItem>;
  const label = typeof i.label === 'string' ? i.label.slice(0, 120) : '';
  const url = typeof i.url === 'string' ? i.url.slice(0, 2000) : '';
  if (!label || !url) return null;
  return {
    label,
    url,
    thumbnail: typeof i.thumbnail === 'string' ? i.thumbnail.slice(0, 2000) : undefined,
    caption: typeof i.caption === 'string' ? i.caption.slice(0, 200) : undefined,
  };
}
